import {
  Component,
  inject,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  MotifButtonModule,
  MotifIconModule,
  MotifModalModule,
  MotifFormsModule,
  MotifTooltipModule,
  MotifModalRef,
  MOTIF_MODAL_DATA,
} from '@ey-xd/ng-motif';
import { IconoirInfoCircle } from '@ey-xd/motif-icon';
import { WorkspaceService } from '@sa/shared/services/workspace.service';
import { CommonService } from '@sa/shared/services/common.service';
import { LoggerService } from '@sa/core/services/logger.service';
import { DeliverableReplicationRequest } from '@sa/shared/interfaces/DeliverableReplication';
import { Subscription } from 'rxjs';

import { ApplicationConstants } from '@sa/shared/constants/ApplicationConstants';

@Component({
  selector: 'app-replicate-deliverable-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MotifButtonModule,
    MotifIconModule,
    MotifModalModule,
    MotifFormsModule,
    MotifTooltipModule,
  ],
  templateUrl: './replicate-deliverable-panel.component.html',
  styleUrls: ['./replicate-deliverable-panel.component.scss'],
})
export class ReplicateDeliverablePanelComponent implements OnInit, OnDestroy {
  filingYears: any[] = [];
  periods: any[] = [];
  selectedFilingYear: any = null;
  selectedPeriod: any = null;

  ApplicationConstants = ApplicationConstants;
  readonly infoIcon = IconoirInfoCircle;
  readonly periodTooltip = ApplicationConstants.TooltipMessages.PeriodDropdown;
  private readonly _workspaceService = inject(WorkspaceService);
  private readonly _commonService = inject(CommonService);
  private readonly _loggerService = inject(LoggerService);
  private readonly _modalRef = inject(MotifModalRef);
  private readonly _modalData = inject(MOTIF_MODAL_DATA);
  private replicationSubscription: Subscription | null = null;
  private periodsSubscription: Subscription | null = null;
  private fiscalYearsSubscription: Subscription | null = null;

  // Sort comparator for periods dropdown
  periodSortComparator(a: any, b: any): number {
    return a.periodId - b.periodId;
  }

  get isReplicateDisabled(): boolean {
    return !this.selectedFilingYear || !this.selectedPeriod;
  }
  constructor() {
  }

  ngOnInit(): void {
    this.loadFiscalYears();
    this.loadPeriods();
  }

  ngOnDestroy(): void {
    if (this.replicationSubscription) {
      this.replicationSubscription.unsubscribe();
      this.replicationSubscription = null;
    }
    if (this.periodsSubscription) {
      this.periodsSubscription.unsubscribe();
      this.periodsSubscription = null;
    }
    if (this.fiscalYearsSubscription) {
      this.fiscalYearsSubscription.unsubscribe();
      this.fiscalYearsSubscription = null;
    }
  }

  private loadFiscalYears(): void {
    const tenantGuid = this._commonService.getState('selectedTenantGuid') || '';
    
    if (!tenantGuid) {
      this._loggerService.logError('Tenant information is missing');
      return;
    }

    this.fiscalYearsSubscription = this._workspaceService.getFiscalYears(tenantGuid).subscribe({
      next: (fiscalYears: any[]) => {
        this.filingYears = fiscalYears;
      },
      error: (err: Error) => {
        this._loggerService.logApiError('Failed to load fiscal years');
        console.error(err);
      }
    });
  }

  private loadPeriods(): void {
    const tenantGuid = this._commonService.getState('selectedTenantGuid') || '';
    
    if (!tenantGuid) {
      this._loggerService.logError('Tenant information is missing');
      return;
    }

    this.periodsSubscription = this._workspaceService.getPeriods(tenantGuid).subscribe({
      next: (periods: any[]) => {
        this.periods = periods;
      },
      error: (err: Error) => {
        this._loggerService.logApiError('Failed to load periods');
        console.error(err);
      }
    });
  }

  onSelectFilingYear(event: any): void {
    this.selectedFilingYear = event;
  }

  onSelectPeriod(event: any): void {
    this.selectedPeriod = event;
  }

  onReplicate(): void {
    if (this.isReplicateDisabled) {
      this._loggerService.logError('Please select both Filing Year and Period');
      return;
    }

    const tenantGuid = this._commonService.getState('selectedTenantGuid') || '';
    
    if (!tenantGuid) {
      this._loggerService.logError('Tenant information is missing');
      return;
    }

    // Use auditYear property which is a string representation of the year
    const complianceYear = parseInt(this.selectedFilingYear.auditYear, 10);

    const request: DeliverableReplicationRequest = {
      compliancePeriod: this.selectedPeriod.periodName,
      complianceYear: complianceYear,
      tenantGuid: tenantGuid,
      complianceCalendar: '' // Will be populated from Excel on backend
    };

    this.replicationSubscription = this._workspaceService
      .replicateDeliverables(tenantGuid, request)
      .subscribe({
        next: (response) => {
          this._loggerService.logSuccess(
            response.message || 'Deliverables replicated successfully'
          );
          this._modalRef.close(response);
        },
        error: (err) => {
          this._loggerService.logApiError(
            err?.error?.message || 'Failed to replicate deliverables'
          );
          console.error('Replication error:', err);
        },
      });
  }
}
